import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUserPreferences } from "./useUserPreferences";
import { convertAmount, sumConverted, type Holding } from "@/lib/currencyTotals";

interface ExchangeRates {
  [currency: string]: number;
}

export interface RefreshOutcome {
  ok: boolean;
  /** Requested currencies that still have no rate. */
  missing: string[];
}

interface UseExchangeRatesReturn {
  rates: ExchangeRates;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  /** True once rates have been fetched (or none were needed), so a missing rate is real, not pending. */
  ready: boolean;
  /** Fetch again and say how it went, so the Refresh button can tell the user. */
  refreshRates: () => Promise<RefreshOutcome>;
  /** null when there is no rate: never treat the raw amount as the base currency (D-056). */
  convertToBase: (amount: number, fromCurrency: string, manualRate?: number | null) => number | null;
  /** Total in the base currency, plus what had to be left out for lack of a rate. */
  sumInBase: (holdings: Holding[]) => ReturnType<typeof sumConverted>;
}

// Supported currencies
export const FIAT_CURRENCIES = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "CHF", symbol: "Fr", name: "Swiss Franc" },
  { code: "CNY", symbol: "¥", name: "Chinese Yuan" },
  { code: "INR", symbol: "₹", name: "Indian Rupee" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
];

export const CRYPTO_CURRENCIES = [
  { code: "BTC", symbol: "₿", name: "Bitcoin" },
  { code: "ETH", symbol: "Ξ", name: "Ethereum" },
  { code: "USDT", symbol: "₮", name: "Tether" },
  { code: "USDC", symbol: "$", name: "USD Coin" },
  { code: "SOL", symbol: "◎", name: "Solana" },
  { code: "XRP", symbol: "✕", name: "XRP" },
  { code: "ADA", symbol: "₳", name: "Cardano" },
  { code: "DOGE", symbol: "Ð", name: "Dogecoin" },
];

export const ALL_CURRENCIES = [...FIAT_CURRENCIES, ...CRYPTO_CURRENCIES];

export const useExchangeRates = (accountCurrencies: string[] = []) => {
  const { preferences } = useUserPreferences();
  const [rates, setRates] = useState<ExchangeRates>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [ready, setReady] = useState(false);

  const baseCurrency = preferences?.currency || "USD";

  const fetchRates = useCallback(async (): Promise<RefreshOutcome> => {
    // Filter out the base currency and get unique currencies
    const uniqueCurrencies = [...new Set(accountCurrencies)]
      .filter(c => c && c !== baseCurrency);
    
    if (uniqueCurrencies.length === 0) {
      setRates({});
      setReady(true);
      return { ok: true, missing: [] };
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('get-exchange-rates', {
        body: { baseCurrency, currencies: uniqueCurrencies }
      });

      if (fnError) throw fnError;

      if (data?.success) {
        const fetched: ExchangeRates = data.rates || {};
        setRates(fetched);
        setLastUpdated(new Date(data.timestamp));
        return { ok: true, missing: uniqueCurrencies.filter((c) => !(c in fetched)) };
      } else {
        throw new Error(data?.error || 'Failed to fetch rates');
      }
    } catch (err) {
      console.error('Error fetching exchange rates:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch exchange rates');
      return { ok: false, missing: uniqueCurrencies };
    } finally {
      setLoading(false);
      setReady(true);
    }
  }, [baseCurrency, accountCurrencies.join(',')]);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const convertToBase = useCallback(
    (amount: number, fromCurrency: string, manualRate?: number | null): number | null =>
      convertAmount(amount, fromCurrency, baseCurrency, rates, manualRate),
    [baseCurrency, rates],
  );

  const sumInBase = useCallback(
    (holdings: Holding[]) => sumConverted(holdings, baseCurrency, rates),
    [baseCurrency, rates],
  );

  return {
    rates,
    loading,
    error,
    lastUpdated,
    ready,
    refreshRates: fetchRates,
    convertToBase,
    sumInBase,
  };
};
